import * as _ from 'lodash';
import { change, formValueSelector } from 'redux-form';
import { diagnostics } from '@blend/ui-common';
import store from 'login-store';
import * as globalWrappers from 'login-utils/globalWrappers';

export type TimelineValue = Mismo.PropertySearchType | Mismo.LoanPurposeType;
const { PropertySearchTypes, LoanPurposeTypes } = globalWrappers.getWindow().SHARED_CONSTANTS;

export const FORM_NAME = 'onboarding';

// set these configs because the onboarding form spans multiple pages
// https://redux-form.com/6.6.3/examples/wizard/
export const FORM_CONFIG = {
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  form: FORM_NAME,
};

export const FIELDS = {
  cashDownPayment: 'cashDownPayment',
  estimatedCreditScore: 'estimatedCreditScore',
  loanAmount: 'loanAmount',
  loanPurpose: 'loanPurpose',
  loanType: 'loanType',
  phoneNumber: 'phoneNumber',
  propertyCounty: 'propertyCounty',
  propertySearchType: 'propertySearchType',
  propertyType: 'propertyType',
  propertyUsageType: 'propertyUsageType',
  propertyValue: 'propertyValue',
  purchasePrice: 'purchasePrice',
  refiPurpose: 'refiPurpose',
  userViewedTermsAndConditionsText: 'userViewedTermsAndConditionsText',
  veteranStatus: 'veteranStatus',
  employmentType: 'employmentType',
  annualIncome: 'annualIncome',
  employmentStartDate: 'employmentStartDate',
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email',
  homePhone: 'homePhone',
  acceptTermsAndEconsentGiven: 'acceptTermsAndEconsentGiven',
  areYouAVeteran: 'areYouAVeteran',
  isRefinancingCurrentCustomerLoan: 'isRefinancingCurrentCustomerLoan',

  // this is a dummy field that is the union of loanPurpose & propertySearchType for use on the timeline page
  _timeline: 'timeline',
};

export type CreditScore = '740' | '720' | '680' | '640' | '619';
export const CREDIT_SCORE_CHOICES = [
  {
    value: 740,
    text: 'Excellent (740 and above)',
  },
  {
    value: 720,
    text: 'Good (700-739)',
  },
  {
    value: 680,
    text: 'Average (660-699)',
  },
  {
    value: 640,
    text: 'Fair (620-659)',
  },
  {
    value: 619,
    text: 'Poor (below 620)',
  },
];

export const selector = formValueSelector<ReduxState>(FORM_NAME);

export function getInitialLoanType(state: ReduxState) {
  return state.onboarding.loanType;
}

export function getLoanType(state: ReduxState) {
  // loanType might not exist in the form if there was a loanType set when /signup was hit
  // or if there's only one loanType available
  const initalLoanType = getInitialLoanType(state);
  const selectedLoanType = selector(state, FIELDS.loanType);
  return selectedLoanType || initalLoanType;
}

export function getLoanTypeFromState() {
  const state = store.getState() as ReduxState;
  return getLoanType(state);
}

export function setLoanType(loanType: LoanType) {
  store.dispatch(change(FORM_NAME, FIELDS.loanType, loanType));
}

/* istanbul ignore next: (UI-5482) */
export function getPropertySearchType(state: ReduxState) {
  return selector(state, FIELDS.propertySearchType);
}

/* istanbul ignore next: (UI-5482) */
export function getLoanPurpose(state: ReduxState) {
  return selector(state, FIELDS.loanPurpose);
}

export function getTimelineValue(state: ReduxState) {
  return selector(state, FIELDS._timeline);
}

export function getValueFromKey(state: ReduxState, key: string) {
  return selector(state, FIELDS[key]);
}

// Translates a timeline value into loanPurpose and propertySearchType.
// The REFINANCE value is a loanPurpose, and refinances have no propertySearchType value;
// All other values are propertySearchTypes that also implicitly map out to PURCHASE
// NOTE: this will only work well for mortgage loans because these values are mortgage-specific. Functionality in
// non-mortgage scenarios is unpredictable
export function translateTimelineValueToFormProps(value: TimelineValue | null) {
  if (!value) {
    return {
      [FIELDS.loanPurpose]: null,
      [FIELDS.propertySearchType]: null,
      [FIELDS._timeline]: null,
      [FIELDS.loanType]: null,
    };
  } else if (_.includes(LoanPurposeTypes.ALL, value)) {
    return {
      [FIELDS.loanPurpose]: value,
      [FIELDS.propertySearchType]: null,
      [FIELDS._timeline]: value,
      [FIELDS.loanType]: 'MORTGAGE',
    };
  } else if (_.find(PropertySearchTypes, { value: value as Mismo.PropertySearchType })) {
    return {
      [FIELDS.loanPurpose]: 'PURCHASE',
      [FIELDS.propertySearchType]: value,
      [FIELDS._timeline]: value,
      [FIELDS.loanType]: 'MORTGAGE',
    };
  }
  diagnostics.error(new Error('Invalid timeline value passed to translateTimelineValueToFormProps'), { value });
  return {};
}
